import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  AlertTriangle,
  Check,
  KeyRound,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Trash2,
  UserMinus,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import {
  cancelPartnerInvite,
  clearHousePartners,
  fetchHousePartners,
  leaveBetHouse,
  removePartnerMember,
  resendPartnerInvite,
  sendPartnerInvite,
} from "../../redux/operatorConsoleSlice";
import { formatRelative } from "../../utils/bettorsDeskHelpers";
import {
  buildLatestRequestsByUser,
  computePartnerActivityStats,
  countPendingInvites,
  formatPartnerCash,
  getRequestInvitedUserId,
  PARTNER_TAB,
  partnerInitials,
  requestStatusLabel,
} from "../../utils/partnersDeskHelpers";
import PartnerDeskBoard from "./PartnerDeskBoard";
import "../../operator-partners.css";

const ConfirmModal = ({
  open,
  title,
  body,
  amountLabel,
  amount,
  confirmLabel,
  busy,
  destructive,
  onClose,
  onConfirm,
}) => {
  if (!open) return null;

  return createPortal(
    <div className="oc-loan-confirm-root" role="dialog" aria-modal="true">
      <button
        type="button"
        className="oc-loan-info-backdrop"
        aria-label="Close"
        onClick={busy ? undefined : onClose}
      />
      <div className="oc-loan-confirm-sheet">
        <h3>{title}</h3>
        <p>{body}</p>
        {amountLabel && amount ? (
          <div className="oc-partners-confirm-amount">
            <em>{amountLabel}</em>
            <strong>{amount}</strong>
          </div>
        ) : null}
        <div className="oc-loan-confirm-foot">
          <button type="button" className="oc-loan-confirm-cancel" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={`oc-loan-confirm-go ${destructive ? "oc-partners-confirm-danger" : ""}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? <Loader2 size={16} className="oc-treasury-modal-spin" /> : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const OperatorPartnersPanel = ({ bet, operatorUserId, onPartnersChange, onLeaveHouse }) => {
  const dispatch = useDispatch();
  const betId = bet?._id;

  const {
    housePartnersData: data,
    housePartnersStatus,
    housePartnersError,
    housePartnersActionLoading: busy,
  } = useSelector((s) => s.operatorConsole);

  const [activeTab, setActiveTab] = useState(PARTNER_TAB.PARTNERS);
  const [inviteCode, setInviteCode] = useState("");
  const [eligibilityChecked, setEligibilityChecked] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState(null);

  const reload = useCallback(() => {
    if (!betId || !operatorUserId) return Promise.resolve();
    return dispatch(fetchHousePartners({ betId, operatorUserId }));
  }, [betId, dispatch, operatorUserId]);

  useEffect(() => {
    reload();
    return () => dispatch(clearHousePartners());
  }, [reload, dispatch]);

  useEffect(() => {
    setEligibilityChecked(false);
    setActiveTab(PARTNER_TAB.PARTNERS);
    setInviteCode("");
  }, [betId]);

  const members = useMemo(() => data?.members ?? [], [data]);
  const requests = useMemo(() => data?.requests ?? [], [data]);
  const checks = useMemo(() => data?.eligibility?.checks ?? [], [data]);
  const isOwner = Boolean(data?.isOwner);
  const pendingRequest = data?.pendingRequest;
  const allowed = Boolean(data?.eligibility?.allowed);
  const inviteAmount = Number(data?.inviteQuote?.amountRequired || 0);

  const currentMember = useMemo(
    () => members.find((member) => String(member.userId) === String(operatorUserId)),
    [members, operatorUserId]
  );

  const currentPartnerIds = useMemo(
    () => new Set(members.map((member) => String(member.userId)).filter(Boolean)),
    [members]
  );

  const pendingInvites = useMemo(() => countPendingInvites(requests), [requests]);

  const pendingInviteUserIds = useMemo(() => {
    const now = Date.now();
    return new Set(
      requests
        .filter(
          (request) =>
            request.status === "pending" && new Date(request.expiresAt).getTime() > now
        )
        .map(getRequestInvitedUserId)
        .filter(Boolean)
    );
  }, [requests]);

  const latestRequestsByUser = useMemo(
    () => buildLatestRequestsByUser(requests),
    [requests]
  );

  const canLeave = Boolean(currentMember && !currentMember.isOwner);
  const actionsAllowed = allowed && !busy;
  const canSendInvite =
    isOwner && actionsAllowed && inviteCode.trim().length > 0 && !pendingRequest;

  const checksPassed = checks.filter((check) => check.ok).length;
  const activityStats = useMemo(() => computePartnerActivityStats(requests), [requests]);

  const deskStatusPill = !eligibilityChecked ? "CHECK" : allowed ? "READY" : "LOCKED";

  const partnerTabs = useMemo(() => {
    const tabs = [
      {
        key: PARTNER_TAB.PARTNERS,
        label: "Partners",
        icon: Users,
        badge: members.length > 0 ? String(members.length) : null,
      },
    ];
    if (isOwner) {
      tabs.push({
        key: PARTNER_TAB.INVITE,
        label: "Add partner",
        icon: UserPlus,
        badge: pendingInvites > 0 ? String(pendingInvites) : null,
      });
    }
    tabs.push({
      key: PARTNER_TAB.ELIGIBILITY,
      label: "Eligibility",
      icon: ShieldCheck,
      badge: eligibilityChecked && !allowed ? "!" : null,
    });
    tabs.push({
      key: PARTNER_TAB.ACTIVITY,
      label: "Activity",
      icon: RefreshCw,
      badge: latestRequestsByUser.length > 0 ? String(latestRequestsByUser.length) : null,
    });
    return tabs;
  }, [
    members.length,
    isOwner,
    pendingInvites,
    eligibilityChecked,
    allowed,
    latestRequestsByUser.length,
  ]);

  useEffect(() => {
    if (!partnerTabs.some((tab) => tab.key === activeTab)) {
      setActiveTab(partnerTabs[0]?.key || PARTNER_TAB.ELIGIBILITY);
    }
  }, [activeTab, partnerTabs]);

  const refreshAfterAction = async (leftHouse = false) => {
    await reload();
    onPartnersChange?.();
    if (leftHouse) {
      onLeaveHouse?.();
    }
  };

  const runAction = async (action, { leftHouse = false } = {}) => {
    try {
      await dispatch(action).unwrap();
      setConfirmConfig(null);
      await refreshAfterAction(leftHouse);
      return true;
    } catch {
      return false;
    }
  };

  const handleCheckEligibility = async () => {
    await reload();
    setEligibilityChecked(true);
  };

  const handleInvite = () => {
    if (!canSendInvite) return;
    setConfirmConfig({
      title: "Send invite?",
      body: "This person will get a request to join this house.",
      amountLabel: "They will pay",
      amount: formatPartnerCash(inviteAmount),
      confirmLabel: "Send invite",
      onConfirm: () =>
        runAction(
          sendPartnerInvite({
            betId,
            operatorUserId,
            inviteCode: inviteCode.trim(),
            expiresInHours: 24,
          })
        ).then((ok) => {
          if (ok) setInviteCode("");
        }),
    });
  };

  if (!betId) return null;

  const loading = housePartnersStatus === "loading" && !data;

  return (
    <section className="oc-panel oc-partners-panel">
      <header className="oc-panel-head oc-partners-head">
        <div>
          <p className="oc-loan-eyebrow">Co-ownership</p>
          <h2>Partner desk</h2>
          <p>Co-owners, invites, and eligibility.</p>
        </div>
        <div className="oc-partners-head-meta">
          <span
            className={`oc-partners-status-pill ${
              deskStatusPill === "READY"
                ? "oc-partners-status-ready"
                : deskStatusPill === "LOCKED"
                  ? "oc-partners-status-locked"
                  : ""
            }`}
          >
            {deskStatusPill}
          </span>
          <button
            type="button"
            className="oc-loan-icon-btn"
            onClick={reload}
            disabled={housePartnersStatus === "loading"}
            aria-label="Refresh partners"
          >
            <RefreshCw
              size={14}
              className={housePartnersStatus === "loading" ? "animate-spin" : ""}
            />
          </button>
        </div>
      </header>

      {loading ? (
        <div className="oc-loan-loading">
          <Loader2 size={22} className="animate-spin" />
          <span>Loading partners…</span>
        </div>
      ) : (
        <>
          <PartnerDeskBoard
            members={members}
            inviteQuote={data?.inviteQuote}
            pendingRequest={pendingRequest}
            pendingInvites={pendingInvites}
            checks={checks}
            eligibilityChecked={eligibilityChecked}
            allowed={allowed}
            checksPassed={checksPassed}
          />

          <div className="oc-partners-workspace">
            <div className="oc-partners-tabs" role="tablist" aria-label="Partner management">
              {partnerTabs.map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    className={`oc-partners-tab ${active ? "oc-partners-tab-active" : ""}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    <Icon size={13} />
                    <span>{tab.label}</span>
                    {tab.badge ? <em>{tab.badge}</em> : null}
                  </button>
                );
              })}
            </div>

            <div className="oc-partners-tab-panel">
              {activeTab === PARTNER_TAB.PARTNERS ? (
                <>
                  <ul className="oc-partners-roster">
                    {members.map((member) => {
                      const isSelf = String(member.userId) === String(operatorUserId);
                      const canRemove = isOwner && !member.isOwner;
                      return (
                        <li
                          key={member.userId}
                          className={`${member.isOwner ? "oc-partners-roster-owner" : ""} ${
                            member.isOwner ? "oc-partners-roster-vip" : ""
                          }`}
                        >
                          <span className="oc-partners-avatar">{partnerInitials(member.name)}</span>
                          <div className="oc-partners-roster-copy">
                            <strong>
                              {member.name}
                              {isSelf ? <em className="oc-partners-you">You</em> : null}
                              <em
                                className={
                                  member.isOwner
                                    ? "oc-partners-role-creator"
                                    : "oc-partners-role-partner"
                                }
                              >
                                {member.isOwner ? "Creator" : "Partner"}
                              </em>
                            </strong>
                            <span>Equal share on exit</span>
                          </div>
                          <div className="oc-partners-share">
                            <em>Share</em>
                            <strong className="tabular-nums">
                              {formatPartnerCash(member.estimatedShare)}
                            </strong>
                          </div>
                          {canRemove ? (
                            <button
                              type="button"
                              className="oc-partners-remove-btn"
                              disabled={busy || !actionsAllowed}
                              aria-label={`Remove ${member.name}`}
                              onClick={() =>
                                setConfirmConfig({
                                  title: "Remove partner?",
                                  body: `${member.name} will leave this house.`,
                                  amountLabel: "They receive",
                                  amount: formatPartnerCash(member.estimatedShare),
                                  confirmLabel: "Remove",
                                  destructive: true,
                                  onConfirm: () =>
                                    runAction(
                                      removePartnerMember({
                                        betId,
                                        operatorUserId,
                                        targetUserId: member.userId,
                                      })
                                    ),
                                })
                              }
                            >
                              <Trash2 size={13} />
                            </button>
                          ) : (
                            <span className="oc-partners-remove-spacer" aria-hidden />
                          )}
                        </li>
                      );
                    })}
                  </ul>

                  {canLeave ? (
                    <button
                      type="button"
                      className="oc-partners-leave-btn"
                      disabled={!actionsAllowed}
                      onClick={() =>
                        setConfirmConfig({
                          title: "Leave this house?",
                          body: "You will no longer manage this house.",
                          amountLabel: "You receive",
                          amount: formatPartnerCash(currentMember?.estimatedShare || 0),
                          confirmLabel: "Leave",
                          destructive: true,
                          onConfirm: () =>
                            runAction(
                              leaveBetHouse({ betId, operatorUserId }),
                              { leftHouse: true }
                            ),
                        })
                      }
                    >
                      <UserMinus size={14} />
                      <span>Leave this house</span>
                      <em className="tabular-nums">
                        {formatPartnerCash(currentMember?.estimatedShare || 0)}
                      </em>
                    </button>
                  ) : (
                    <p className="oc-partners-footnote">The creator cannot leave this house.</p>
                  )}
                </>
              ) : null}

              {activeTab === PARTNER_TAB.INVITE ? (
                isOwner ? (
                  <>
                    <p className="oc-partners-lead">
                      New partner pays {formatPartnerCash(inviteAmount)} · invite expires in 24h.
                    </p>
                    <label className="oc-partners-input-row">
                      <KeyRound size={14} />
                      <input
                        type="text"
                        value={inviteCode}
                        onChange={(e) => setInviteCode(e.target.value.trim())}
                        placeholder="Enter invite code"
                        autoCapitalize="characters"
                        disabled={!actionsAllowed || pendingRequest}
                      />
                    </label>
                    <button
                      type="button"
                      className="oc-loan-primary"
                      disabled={!canSendInvite}
                      onClick={handleInvite}
                    >
                      {busy ? (
                        <Loader2 size={16} className="oc-treasury-modal-spin" />
                      ) : pendingRequest ? (
                        "Invite waiting"
                      ) : (
                        "Send invite"
                      )}
                    </button>
                  </>
                ) : (
                  <p className="oc-partners-empty">
                    Only the house creator can send partner invites.
                  </p>
                )
              ) : null}

              {activeTab === PARTNER_TAB.ELIGIBILITY ? (
                !eligibilityChecked ? (
                  <div className="oc-partners-eligibility-intro">
                    <p>
                      House must be paused with no open tickets, locked liability, or active loan
                      before partner changes.
                    </p>
                    <button
                      type="button"
                      className="oc-loan-primary"
                      disabled={housePartnersStatus === "loading"}
                      onClick={handleCheckEligibility}
                    >
                      {housePartnersStatus === "loading" ? (
                        <Loader2 size={16} className="oc-treasury-modal-spin" />
                      ) : (
                        <>
                          <ShieldCheck size={15} />
                          Check eligibility
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="oc-partners-eligibility-results">
                    <div
                      className={`oc-partners-eligibility-strip ${
                        allowed ? "oc-partners-eligibility-pass" : "oc-partners-eligibility-fail"
                      }`}
                    >
                      {allowed ? <Check size={16} /> : <X size={16} />}
                      <div>
                        <strong>
                          {allowed ? "Eligible for partner changes" : "Not eligible yet"}
                        </strong>
                        <span>
                          {checksPassed}/{checks.length || 0} requirements met
                        </span>
                      </div>
                    </div>
                    <ul className="oc-partners-checks">
                      {checks.map((check) => (
                        <li key={check.id} className={check.ok ? "oc-partners-check-pass" : ""}>
                          <span className="oc-partners-check-icon">
                            {check.ok ? <Check size={11} /> : <X size={11} />}
                          </span>
                          <div>
                            <strong>{check.title}</strong>
                            {!check.ok && check.detail ? <p>{check.detail}</p> : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      className="oc-partners-recheck"
                      disabled={housePartnersStatus === "loading"}
                      onClick={handleCheckEligibility}
                    >
                      <RefreshCw
                        size={12}
                        className={housePartnersStatus === "loading" ? "animate-spin" : ""}
                      />
                      Recheck eligibility
                    </button>
                  </div>
                )
              ) : null}

              {activeTab === PARTNER_TAB.ACTIVITY ? (
                latestRequestsByUser.length ? (
                  <>
                    {activityStats.decided > 0 ? (
                      <div
                        className="oc-partners-activity-mix"
                        aria-label="Invite outcome mix"
                        title={`${activityStats.accepted} accepted · ${activityStats.pending} pending · ${activityStats.expired + activityStats.cancelled + activityStats.rejected} closed without join`}
                      >
                        {activityStats.accepted > 0 ? (
                          <span
                            style={{
                              width: `${(activityStats.accepted / activityStats.total) * 100}%`,
                              background: "#46c479",
                            }}
                          />
                        ) : null}
                        {activityStats.pending > 0 ? (
                          <span
                            style={{
                              width: `${(activityStats.pending / activityStats.total) * 100}%`,
                              background: "#ffb74d",
                            }}
                          />
                        ) : null}
                        {activityStats.expired + activityStats.cancelled + activityStats.rejected > 0 ? (
                          <span
                            style={{
                              width: `${
                                ((activityStats.expired +
                                  activityStats.cancelled +
                                  activityStats.rejected) /
                                  activityStats.total) *
                                100
                              }%`,
                              background: "#ef5350",
                            }}
                          />
                        ) : null}
                      </div>
                    ) : null}
                    <ul className="oc-partners-activity">
                    {latestRequestsByUser.slice(0, 12).map((request) => {
                      const invitedId = getRequestInvitedUserId(request);
                      const invited = request.invitedUser || {};
                      const pending =
                        request.status === "pending" &&
                        new Date(request.expiresAt) > new Date();
                      const isCurrentPartner = currentPartnerIds.has(invitedId);
                      const hasPendingForSameUser =
                        request.status !== "pending" && pendingInviteUserIds.has(invitedId);

                      return (
                        <li key={request._id}>
                          <div className="oc-partners-activity-copy">
                            <strong>{invited.name || invited.email || "Invited user"}</strong>
                            <span>
                              {requestStatusLabel(request.status)} ·{" "}
                              {formatPartnerCash(request.amountRequired)}
                              {pending
                                ? ` · expires ${formatRelative(request.expiresAt)}`
                                : ""}
                            </span>
                          </div>
                          {isOwner && pending ? (
                            <button
                              type="button"
                              className="oc-partners-ghost-btn"
                              disabled={busy}
                              onClick={() =>
                                setConfirmConfig({
                                  title: "Cancel invite?",
                                  body: "The house can continue after this invite is cancelled.",
                                  confirmLabel: "Cancel invite",
                                  destructive: true,
                                  onConfirm: () =>
                                    runAction(
                                      cancelPartnerInvite({
                                        betId,
                                        operatorUserId,
                                        requestId: request._id,
                                      })
                                    ),
                                })
                              }
                            >
                              Cancel
                            </button>
                          ) : null}
                          {isOwner && !pending && !hasPendingForSameUser && !isCurrentPartner ? (
                            <button
                              type="button"
                              className="oc-partners-ghost-btn"
                              disabled={busy || !actionsAllowed}
                              onClick={() =>
                                setConfirmConfig({
                                  title: "Send invite again?",
                                  body: "The old request stays in history. A new one will be sent.",
                                  amountLabel: "They will pay",
                                  amount: formatPartnerCash(inviteAmount),
                                  confirmLabel: "Send again",
                                  onConfirm: () =>
                                    runAction(
                                      resendPartnerInvite({
                                        betId,
                                        operatorUserId,
                                        requestId: request._id,
                                        expiresInHours: 24,
                                      })
                                    ),
                                })
                              }
                            >
                              Send again
                            </button>
                          ) : null}
                          {isOwner && !pending && (hasPendingForSameUser || isCurrentPartner) ? (
                            <span className="oc-partners-wait-pill">
                              {isCurrentPartner ? "Partner" : "Waiting"}
                            </span>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                  </>
                ) : (
                  <p className="oc-partners-empty">No invites or partner changes yet.</p>
                )
              ) : null}
            </div>
          </div>

          {housePartnersError ? (
            <div className="oc-alert">
              <AlertTriangle size={14} />
              <div>
                <strong>Partner action failed</strong>
                <p>{String(housePartnersError)}</p>
              </div>
            </div>
          ) : null}
        </>
      )}

      <ConfirmModal
        open={Boolean(confirmConfig)}
        title={confirmConfig?.title}
        body={confirmConfig?.body}
        amountLabel={confirmConfig?.amountLabel}
        amount={confirmConfig?.amount}
        confirmLabel={confirmConfig?.confirmLabel || "Confirm"}
        destructive={confirmConfig?.destructive}
        busy={busy}
        onClose={() => {
          if (!busy) setConfirmConfig(null);
        }}
        onConfirm={confirmConfig?.onConfirm}
      />
    </section>
  );
};

export default OperatorPartnersPanel;
